import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Loader2, AlertCircle, ArrowLeft, TestTube2, GripVertical } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import FilterBuilder from '../components/FilterBuilder';
import FormulaEditor from '../components/FormulaEditor';

const CondicionesPage = () => {
  const { contratoId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editingCondicion, setEditingCondicion] = useState(null);
  const [testCondicion, setTestCondicion] = useState(null);
  const [testCups, setTestCups] = useState('');
  const [draggedId, setDraggedId] = useState(null);

  const { data: contrato, isLoading: isLoadingContrato, error: errorContrato } = useQuery({
    queryKey: ['contrato', contratoId],
    queryFn: async () => {
      const response = await axios.get(`/api/contratos/${contratoId}`);
      return response.data;
    },
  });

  const { data: condiciones, isLoading: isLoadingCondiciones, error: errorCondiciones } = useQuery({
    queryKey: ['condiciones', contratoId],
    queryFn: async () => {
      const response = await axios.get(`/api/contratos/${contratoId}/condiciones`);
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`/api/contratos/${contratoId}/condiciones`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Condición creada exitosamente');
      queryClient.invalidateQueries(['condiciones', contratoId]);
      setShowModal(false);
      setEditingCondicion(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error creando condición');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`/api/contratos/${contratoId}/condiciones/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Condición actualizada exitosamente');
      queryClient.invalidateQueries(['condiciones', contratoId]);
      setShowModal(false);
      setEditingCondicion(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error actualizando condición');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`/api/contratos/${contratoId}/condiciones/${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Condición eliminada exitosamente');
      queryClient.invalidateQueries(['condiciones', contratoId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error eliminando condición');
    },
  });

  const reordenarMutation = useMutation({
    mutationFn: async (ordenIds) => {
      const response = await axios.put(`/api/contratos/${contratoId}/condiciones/reordenar`, { orden: ordenIds });
      return response.data;
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error reordenando condiciones');
      queryClient.invalidateQueries(['condiciones', contratoId]);
    },
  });

  const handleDrop = (targetId) => {
    if (draggedId === null || draggedId === targetId) return;
    const actuales = queryClient.getQueryData(['condiciones', contratoId]) || [];
    const fromIndex = actuales.findIndex((c) => c.id === draggedId);
    const toIndex = actuales.findIndex((c) => c.id === targetId);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordenadas = [...actuales];
    const [movida] = reordenadas.splice(fromIndex, 1);
    reordenadas.splice(toIndex, 0, movida);

    queryClient.setQueryData(['condiciones', contratoId], reordenadas);
    reordenarMutation.mutate(reordenadas.map((c) => c.id));
    setDraggedId(null);
  };

  const testMatchMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/api/contratos/${contratoId}/condiciones/${testCondicion}/test-match`, {
        cups: testCups
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || `Aplica: ${data.aplica}`);
      setTestCondicion(null);
      setTestCups('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error probando condición');
    },
  });

  const handleOpenModal = (condicion = null) => {
    setEditingCondicion(condicion);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Está seguro de eliminar esta condición?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatFiltros = (filtros) => {
    if (!filtros || !filtros.rules || filtros.rules.length === 0) return 'Sin filtros (aplica a todos)';
    const countConditions = (rules) => rules.reduce((acc, r) => acc + (r.type === 'condition' ? 1 : countConditions(r.rules || [])), 0);
    return `${countConditions(filtros.rules)} condición(es)`;
  };

  if (isLoadingContrato || isLoadingCondiciones) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (errorContrato || errorCondiciones) {
    return (
      <div className="flex items-center space-x-2 p-4 bg-red-50 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>Error cargando datos</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/contratos')} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-2">
            <ArrowLeft className="w-4 h-4" /> Volver a contratos
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Condiciones de {contrato?.nombre}</h1>
          <p className="mt-2 text-gray-600">
            Se evalúan por prioridad (arrastra las filas para reordenar). La primera condición que coincide se aplica y las demás se ignoran.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Nueva Condición</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        {condiciones && condiciones.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay condiciones registradas para este contrato.
          </div>
        )}

        {condiciones && condiciones.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 w-8"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filtros</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valores</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {condiciones.map((condicion, idx) => (
                  <tr
                    key={condicion.id}
                    className="hover:bg-gray-50"
                    draggable
                    onDragStart={() => setDraggedId(condicion.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(condicion.id)}
                  >
                    <td className="px-2 py-4 text-gray-400 cursor-move" title="Arrastrar para reordenar">
                      <GripVertical className="w-4 h-4" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {condicion.nombre_condicion}
                      <div className="text-xs text-gray-400 font-normal">Prioridad: {idx + 1}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFiltros(condicion.filtros_json)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {condicion.modo_calculo === 'formula' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">Fórmula</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">Valores fijos</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {condicion.modo_calculo === 'formula' ? (
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all">{condicion.formula_calculo?.substring(0, 50)}{condicion.formula_calculo?.length > 50 ? '...' : ''}</code>
                      ) : (
                        <div className="space-y-1">
                          {Number(condicion.porcentaje_extra_broker) > 0 && <div className="text-xs">% Broker: {Number(condicion.porcentaje_extra_broker).toFixed(4)}</div>}
                          {Number(condicion.comision_emwh) > 0 && <div className="text-xs">€/MWh: {Number(condicion.comision_emwh).toFixed(4)}</div>}
                          {Number(condicion.comision_ecups) > 0 && <div className="text-xs">€/CUPS: {Number(condicion.comision_ecups).toFixed(2)}</div>}
                          {!(Number(condicion.porcentaje_extra_broker) > 0 || Number(condicion.comision_emwh) > 0 || Number(condicion.comision_ecups) > 0) && <span className="text-xs text-gray-400 italic">Sin comisiones</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${condicion.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {condicion.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setTestCondicion(condicion.id)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Probar condición"
                        >
                          <TestTube2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenModal(condicion)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(condicion.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <CondicionModal
          condicion={editingCondicion}
          onClose={() => { setShowModal(false); setEditingCondicion(null); }}
          onSubmit={(formData) => {
            if (editingCondicion) {
              updateMutation.mutate({ id: editingCondicion.id, data: formData });
            } else {
              createMutation.mutate(formData);
            }
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {testCondicion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Probar condición</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">CUPS</label>
            <input
              type="text"
              value={testCups}
              onChange={(e) => setTestCups(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              placeholder="ES0000000000000000XX"
            />
            <div className="flex justify-end space-x-3">
              <button onClick={() => setTestCondicion(null)} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg">Cancelar</button>
              <button
                onClick={() => testMatchMutation.mutate()}
                disabled={testMatchMutation.isPending || !testCups.trim()}
                className="px-4 py-2 text-sm text-white bg-primary rounded-lg disabled:opacity-50"
              >Probar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function CondicionModal({ condicion, onClose, onSubmit, isLoading }) {
  const [formData, setFormData] = useState({
    nombre_condicion: condicion?.nombre_condicion || '',
    filtros_json: condicion?.filtros_json || { logic: 'AND', rules: [] },
    modo_calculo: condicion?.modo_calculo || 'valores_fijos',
    porcentaje_extra_broker: condicion?.porcentaje_extra_broker ?? '',
    comision_emwh: condicion?.comision_emwh ?? '',
    comision_ecups: condicion?.comision_ecups ?? '',
    formula_calculo: condicion?.formula_calculo || '',
    activo: condicion?.activo ?? true
  });
  const [filterValid, setFilterValid] = useState(true);
  const [filterError, setFilterError] = useState(null);
  const [formulaValid, setFormulaValid] = useState(true);
  const [formulaError, setFormulaError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.nombre_condicion.trim()) {
      toast.error('El nombre de la condición es obligatorio');
      return;
    }

    if (!filterValid) {
      toast.error(filterError || 'Los filtros contienen errores');
      return;
    }

    if (formData.modo_calculo === 'formula') {
      if (!formData.formula_calculo.trim()) {
        toast.error('Debes proporcionar una fórmula');
        return;
      }
      if (!formulaValid) {
        toast.error(formulaError || 'La fórmula contiene errores');
        return;
      }
    }

    const payload = { ...formData };
    if (payload.modo_calculo === 'formula') {
      payload.porcentaje_extra_broker = null;
      payload.comision_emwh = null;
      payload.comision_ecups = null;
    } else {
      payload.formula_calculo = null;
      payload.formula_columnas = null;
      payload.porcentaje_extra_broker = payload.porcentaje_extra_broker ? parseFloat(payload.porcentaje_extra_broker) : null;
      payload.comision_emwh = payload.comision_emwh ? parseFloat(payload.comision_emwh) : null;
      payload.comision_ecups = payload.comision_ecups ? parseFloat(payload.comision_ecups) : null;
    }

    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">{condicion ? 'Editar Condición' : 'Nueva Condición'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la condición *</label>
            <input
              type="text"
              value={formData.nombre_condicion}
              onChange={(e) => setFormData({ ...formData, nombre_condicion: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Ej: CUPS de Madrid con alto consumo"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filtros de aplicación</label>
            <FilterBuilder
              value={formData.filtros_json}
              onChange={(value) => setFormData({ ...formData, filtros_json: value })}
              onValidate={(valid, error) => { setFilterValid(valid); setFilterError(error); }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Modo de cálculo</label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="valores_fijos"
                  checked={formData.modo_calculo === 'valores_fijos'}
                  onChange={() => setFormData({ ...formData, modo_calculo: 'valores_fijos' })}
                  className="form-radio text-primary"
                />
                <span className="ml-2">Valores fijos</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="formula"
                  checked={formData.modo_calculo === 'formula'}
                  onChange={() => setFormData({ ...formData, modo_calculo: 'formula' })}
                  className="form-radio text-primary"
                />
                <span className="ml-2">Fórmula personalizada</span>
              </label>
            </div>
          </div>

          {formData.modo_calculo === 'valores_fijos' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">% Extra Broker</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.porcentaje_extra_broker}
                  onChange={(e) => setFormData({ ...formData, porcentaje_extra_broker: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">€/MWh</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.comision_emwh}
                  onChange={(e) => setFormData({ ...formData, comision_emwh: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">€/CUPS</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.comision_ecups}
                  onChange={(e) => setFormData({ ...formData, comision_ecups: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="0.00"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fórmula de cálculo</label>
              <FormulaEditor
                value={formData.formula_calculo}
                onChange={(value) => setFormData({ ...formData, formula_calculo: value })}
                onValidation={(valid, error) => { setFormulaValid(valid !== false); setFormulaError(error); }}
              />
            </div>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="activo"
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="activo" className="text-sm font-medium text-gray-700">Activa</label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CondicionesPage;
